/**
 * File: authService.ts
 * Path: apps/admin/src/modules/auth/authService.ts
 * Description: Firebase Authentication service for user login, logout, and session management.
 */

import {
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile as firebaseUpdateProfile,
  updatePassword as firebaseUpdatePassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  User,
  UserCredential,
  ActionCodeSettings,
} from "firebase/auth";
import { auth } from "@idk/config";

export interface UserRole {
  isAdmin: boolean;
  isEditor: boolean;
}

export const AuthService = {
  login: async (email: string, pass: string): Promise<UserCredential> => {
    try {
      return await signInWithEmailAndPassword(auth, email, pass);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  },

  loginWithGoogle: async (): Promise<UserCredential> => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });

      return await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google login failed:", error);
      throw error;
    }
  },

  /**
   * Sends a sign-in email link to the user.
   * @param email The user's email address.
   * @param actionCodeSettings Configuration for the email link redirect.
   */
  sendEmailLink: async (
    email: string,
    actionCodeSettings: ActionCodeSettings,
  ): Promise<void> => {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    } catch (error) {
      console.error("Failed to send email link:", error);
      throw error;
    }
  },

  /**
   * Checks if an incoming link is a valid email sign-in link.
   * @param emailLink The URL the user clicked to sign in.
   */
  checkIsSignInWithEmailLink: (emailLink: string): boolean => {
    return isSignInWithEmailLink(auth, emailLink);
  },

  /**
   * Completes the sign-in process when the user clicks the email link.
   * @param email The user's email address (must match the one used to request the link).
   * @param emailLink The URL the user clicked.
   */
  verifyAndSignInWithEmailLink: async (
    email: string,
    emailLink: string,
  ): Promise<UserCredential> => {
    try {
      return await signInWithEmailLink(auth, email, emailLink);
    } catch (error) {
      console.error("Failed to sign in with email link:", error);
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  },

  waitForAuth: (): Promise<User | null> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  },

  getCurrentUser: (): User | null => {
    return auth.currentUser;
  },

  resetPassword: async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset failed:", error);
      throw error;
    }
  },

  updateProfile: async (
    displayName: string,
    photoURL?: string,
  ): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error("No user logged in");

    try {
      await firebaseUpdateProfile(user, { displayName, photoURL });
    } catch (error) {
      console.error("Profile update failed:", error);
      throw error;
    }
  },

  updatePassword: async (newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error("No user logged in");

    try {
      await firebaseUpdatePassword(user, newPassword);
    } catch (error) {
      console.error("Password update failed:", error);
      throw error;
    }
  },

  getUserRole: async (): Promise<UserRole> => {
    const user = auth.currentUser;
    if (!user) return { isAdmin: false, isEditor: false };

    try {
      const tokenResult = await user.getIdTokenResult(true);
      return {
        isAdmin: !!tokenResult.claims.admin,
        isEditor: !!tokenResult.claims.editor,
      };
    } catch (error) {
      console.error("Error fetching roles:", error);
      return { isAdmin: false, isEditor: false };
    }
  },
};
